// Excel benzeri formül motoru
import { columnToIndex, indexToColumn, cellToAddress, addressToCell } from './cellUtils';

// Hücre referansını parse et
const parseCellReference = (reference) => {
  const match = reference.match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;
  
  const col = columnToIndex(match[1]);
  const row = parseInt(match[2]) - 1;
  return { row, col };
};

// Aralık referansını parse et
const parseRangeReference = (range) => {
  const [start, end] = range.split(':');
  const startRef = parseCellReference(start);
  const endRef = parseCellReference(end);
  
  if (!startRef || !endRef) return null;
  
  return {
    startRow: Math.min(startRef.row, endRef.row),
    endRow: Math.max(startRef.row, endRef.row),
    startCol: Math.min(startRef.col, endRef.col),
    endCol: Math.max(startRef.col, endRef.col)
  };
};

// Hücre değerini al
const getCellValue = (grid, row, col) => {
  if (row < 0 || col < 0 || !grid[row] || !grid[row][col]) return 0;
  
  const cell = grid[row][col];
  if (!cell) return 0;
  
  // Hücrenin değerini al (formül varsa hesaplanmış değeri, yoksa direkt değeri)
  const value = cell.value || '';
  
  // Sayısal değer kontrolü
  const numValue = parseFloat(value);
  return isNaN(numValue) ? 0 : numValue;
};

// Hücre değerini string olarak al
const getCellValueAsString = (grid, row, col) => {
  if (row < 0 || col < 0 || !grid[row] || !grid[row][col]) return '';
  const cell = grid[row][col];
  if (!cell) return '';
  return cell.value || '';
};

// Aralık değerlerini al
const getRangeValues = (grid, range) => {
  const values = [];
  for (let row = range.startRow; row <= range.endRow; row++) {
    for (let col = range.startCol; col <= range.endCol; col++) {
      values.push(getCellValue(grid, row, col));
    }
  }
  return values;
};

// YENİ: Dinamik formül hesaplama - seçili hücreye göre formülü adapte et
const adaptFormulaForCell = (formula, targetRow, targetCol, sourceRow, sourceCol) => {
  if (!formula.startsWith('=')) return formula;
  
  // Hücre referanslarını bul ve güncelle
  const cellRefRegex = /([A-Z]+)(\d+)/g;
  let adaptedFormula = formula;
  let match;
  
  while ((match = cellRefRegex.exec(formula)) !== null) {
    const originalRef = match[0];
    const colLetter = match[1];
    const rowNum = parseInt(match[2]);
    
    // Kaynak hücrenin koordinatlarını hesapla
    const sourceColIndex = columnToIndex(colLetter);
    const sourceRowIndex = rowNum - 1;
    
    // Hedef hücreye göre offset hesapla
    const rowOffset = targetRow - sourceRow;
    const colOffset = targetCol - sourceCol;
    
    // Yeni koordinatları hesapla
    const newRowIndex = sourceRowIndex + rowOffset;
    const newColIndex = sourceColIndex + colOffset;
    
    // Geçerli koordinat kontrolü
    if (newRowIndex >= 0 && newColIndex >= 0) {
      const newColLetter = indexToColumn(newColIndex);
      const newRowNum = newRowIndex + 1;
      const newRef = `${newColLetter}${newRowNum}`;
      
      // Formüldeki referansı güncelle
      adaptedFormula = adaptedFormula.replace(originalRef, newRef);
    }
  }
  
  return adaptedFormula;
};

// YENİ: Çoklu hücre seçimi için formül uygulama
const applyFormulaToSelection = (formula, selection, grid) => {
  const { startRow, startCol, endRow, endCol } = selection;
  const minRow = Math.min(startRow, endRow);
  const maxRow = Math.max(startRow, endRow);
  const minCol = Math.min(startCol, endCol);
  const maxCol = Math.max(startCol, endCol);
  
  const results = [];
  
  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      // Formülü bu hücre için adapte et
      const adaptedFormula = adaptFormulaForCell(formula, row, col, minRow, minCol);
      
      // Formülü hesapla
      try {
        const result = evaluateFormula(adaptedFormula, grid);
        results.push({
          row,
          col,
          formula: adaptedFormula,
          value: result
        });
      } catch (error) {
        results.push({
          row,
          col,
          formula: adaptedFormula,
          value: '#ERROR!'
        });
      }
    }
  }
  
  return results;
};

// YENİ: Bağımlılık grafiği oluştur
const buildDependencyGraph = (grid) => {
  const dependencies = new Map();
  
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const cell = grid[row][col];
      if (cell && cell.formula) {
        const cellKey = `${row}-${col}`;
        const refs = extractCellReferences(cell.formula);
        dependencies.set(cellKey, refs);
      }
    }
  }
  
  return dependencies;
};

// YENİ: Hücre referanslarını çıkar
const extractCellReferences = (formula) => {
  const refs = [];
  const cellRefRegex = /([A-Z]+)(\d+)/g;
  let match;
  
  while ((match = cellRefRegex.exec(formula)) !== null) {
    const colLetter = match[1];
    const rowNum = parseInt(match[2]);
    const colIndex = columnToIndex(colLetter);
    const rowIndex = rowNum - 1;
    refs.push({ row: rowIndex, col: colIndex });
  }
  
  return refs;
};

// YENİ: Topolojik sıralama ile formül hesaplama sırası
const getCalculationOrder = (dependencies) => {
  const visited = new Set();
  const temp = new Set();
  const order = [];
  
  const visit = (cellKey) => {
    if (temp.has(cellKey)) {
      throw new Error('Circular reference detected');
    }
    if (visited.has(cellKey)) return;
    
    temp.add(cellKey);
    
    const deps = dependencies.get(cellKey) || [];
    for (const dep of deps) {
      const depKey = `${dep.row}-${dep.col}`;
      visit(depKey);
    }
    
    temp.delete(cellKey);
    visited.add(cellKey);
    order.push(cellKey);
  };
  
  for (const cellKey of dependencies.keys()) {
    if (!visited.has(cellKey)) {
      visit(cellKey);
    }
  }
  
  return order;
};

// Matematiksel fonksiyonlar
const mathFunctions = {
  SUM: (args) => {
    return args.reduce((sum, val) => sum + (isNaN(val) ? 0 : val), 0);
  },
  
  PRODUCT: (args) => {
    return args.reduce((product, val) => {
      const num = isNaN(val) ? 1 : val;
      return product * num;
    }, 1);
  },
  
  AVERAGE: (args) => {
    const validArgs = args.filter(val => !isNaN(val));
    return validArgs.length > 0 ? validArgs.reduce((sum, val) => sum + val, 0) / validArgs.length : 0;
  },
  
  COUNT: (args) => {
    return args.filter(val => val !== '' && val !== null && val !== undefined).length;
  },
  
  COUNTA: (args) => {
    return args.filter(val => val !== '' && val !== null && val !== undefined).length;
  },
  
  COUNTBLANK: (args) => {
    return args.filter(val => val === '' || val === null || val === undefined).length;
  },
  
  MAX: (args) => {
    const validArgs = args.filter(val => !isNaN(val));
    return validArgs.length > 0 ? Math.max(...validArgs) : 0;
  },
  
  MIN: (args) => {
    const validArgs = args.filter(val => !isNaN(val));
    return validArgs.length > 0 ? Math.min(...validArgs) : 0;
  },
  
  ROUND: (args) => {
    if (args.length < 1) return 0;
    const num = args[0];
    const decimals = args[1] || 0;
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  },
  
  ROUNDUP: (args) => {
    if (args.length < 1) return 0;
    const num = args[0];
    const decimals = args[1] || 0;
    return Math.ceil(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  },
  
  ROUNDDOWN: (args) => {
    if (args.length < 1) return 0;
    const num = args[0];
    const decimals = args[1] || 0;
    return Math.floor(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  },
  
  ABS: (args) => {
    return Math.abs(args[0] || 0);
  },
  
  SQRT: (args) => {
    const num = args[0] || 0;
    return num >= 0 ? Math.sqrt(num) : 0;
  },
  
  POWER: (args) => {
    if (args.length < 2) return 0;
    return Math.pow(args[0], args[1]);
  },
  
  MOD: (args) => {
    if (args.length < 2) return 0;
    return args[0] % args[1];
  },
  
  INT: (args) => {
    return Math.floor(args[0] || 0);
  },
  
  RAND: () => {
    return Math.random();
  },
  
  RANDBETWEEN: (args) => {
    if (args.length < 2) return 0;
    const min = Math.ceil(args[0]);
    const max = Math.floor(args[1]);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  },
  
  SIGN: (args) => {
    const num = args[0] || 0;
    return num > 0 ? 1 : num < 0 ? -1 : 0;
  }
};

// Metin fonksiyonları
const textFunctions = {
  CONCATENATE: (args) => {
    return args.map(val => String(val)).join('');
  },
  
  LEFT: (args) => {
    const text = String(args[0] || '');
    const numChars = args[1] || 1;
    return text.substring(0, numChars);
  },
  
  RIGHT: (args) => {
    const text = String(args[0] || '');
    const numChars = args[1] || 1;
    return text.substring(text.length - numChars);
  },
  
  LEN: (args) => {
    return String(args[0] || '').length;
  },
  
  UPPER: (args) => {
    return String(args[0] || '').toUpperCase();
  },
  
  LOWER: (args) => {
    return String(args[0] || '').toLowerCase();
  },
  
  TRIM: (args) => {
    return String(args[0] || '').trim();
  }
};

// Mantık fonksiyonları
const logicFunctions = {
  IF: (args) => {
    if (args.length < 2) return false;
    return args[0] ? args[1] : (args[2] || false);
  },
  
  AND: (args) => {
    return args.every(val => val);
  },
  
  OR: (args) => {
    return args.some(val => val);
  },
  
  NOT: (args) => {
    return !args[0];
  }
};

// Tarih fonksiyonları
const dateFunctions = {
  TODAY: () => {
    return new Date().toISOString().split('T')[0];
  },
  
  NOW: () => {
    return new Date().toISOString();
  }
};

// Tüm fonksiyonları birleştir
const allFunctions = {
  ...mathFunctions,
  ...textFunctions,
  ...logicFunctions,
  ...dateFunctions
};

// Gelişmiş formül değerlendirme
const evaluateFormula = (formula, grid) => {
  if (!formula || !formula.startsWith('=')) return formula;
  
  try {
    const cleanFormula = formula.substring(1).trim();
    
    // Fonksiyon çağrılarını bul ve değerlendir
    const functionPattern = /([A-Z]+)\s*\(([^)]*)\)/g;
    let match;
    let evaluatedFormula = cleanFormula;
    
    // Fonksiyonları değerlendir
    while ((match = functionPattern.exec(cleanFormula)) !== null) {
      const functionName = match[1];
      const params = match[2];
      
      // Parametreleri parse et
      let args = [];
      
      // Aralık kontrolü (örn: A1:C3)
      if (params.includes(':')) {
        const rangeMatch = params.match(/([A-Z]+\d+):([A-Z]+\d+)/);
        if (rangeMatch) {
          const range = parseRangeReference(`${rangeMatch[1]}:${rangeMatch[2]}`);
          if (range) {
            args = getRangeValues(grid, range);
          }
        }
      } else {
        // Virgülle ayrılmış parametreler
        const paramList = params.split(',').map(p => p.trim());
        args = paramList.map(param => {
          // Hücre referansı kontrolü
          const cellRef = parseCellReference(param);
          if (cellRef) {
            return getCellValue(grid, cellRef.row, cellRef.col);
          }
          // Sayısal değer kontrolü
          const numValue = parseFloat(param);
          return isNaN(numValue) ? param : numValue;
        });
      }
      
      // Fonksiyonu çalıştır
      if (allFunctions[functionName]) {
        const result = allFunctions[functionName](args);
        evaluatedFormula = evaluatedFormula.replace(match[0], result);
      } else {
        console.warn(`Bilinmeyen fonksiyon: ${functionName}`);
        return '#NAME?';
      }
    }
    
    // Kalan hücre referanslarını değerlerle değiştir
    evaluatedFormula = evaluatedFormula.replace(/[A-Z]+\d+/g, (match) => {
      const ref = parseCellReference(match);
      if (ref) {
        const value = getCellValue(grid, ref.row, ref.col);
        return value;
      }
      return 0;
    });
    
    // Basit matematiksel işlemleri değerlendir
    const safeEval = (expr) => {
      // Güvenlik kontrolü - sadece sayılar ve matematiksel operatörler
      const allowedChars = /^[0-9+\-*/().\s,]+$/;
      if (!allowedChars.test(expr)) {
        console.warn('Güvenlik hatası, geçersiz karakterler:', expr);
        return '#VALUE!';
      }
      try {
        // Virgülü noktaya çevir (ondalık ayracı için)
        const normalizedExpr = expr.replace(/,/g, '.');
        const result = eval(normalizedExpr);
        return isNaN(result) ? '#VALUE!' : result;
      } catch (error) {
        console.error('Eval hatası:', error);
        return '#ERROR!';
      }
    };
    
    const result = safeEval(evaluatedFormula);
    return result;
    
  } catch (error) {
    console.error('Formül hatası:', error);
    return '#ERROR!';
  }
};

// Test fonksiyonu - genişletilmiş
const testFormulaEngine = () => {
  console.log('=== FORMÜL MOTORU TEST ===');
  
  // Test grid - görüntüdeki verilerle benzer
  const testGrid = [
    [{ value: '10' }, { value: '20' }, { value: '30' }, { value: '3' }, { value: '1' }, { value: '7' }],
    [{ value: '5' }, { value: '15' }, { value: '25' }, { value: '3' }, { value: '2' }, { value: '7' }],
    [{ value: '100' }, { value: '200' }, { value: '300' }, { value: '1' }, { value: '2' }, { value: '15' }]
  ];
  
  // Test formülleri - görüntüdeki formüllerle benzer
  const testFormulas = [
    '=PRODUCT(D1:F1)',  // 3*1*7 = 21
    '=SUM(A1:C1)',      // 10+20+30 = 60
    '=AVERAGE(A1:C3)',  // Ortalama
    '=MAX(A1:C3)',      // 300
    '=MIN(A1:C3)',      // 5
    '=A1*B1+C1',        // 10*20+30 = 230
    '=D1*E1*F1',        // 3*1*7 = 21
    '=TODAY()',
    '=COUNT(A1:F3)'
  ];
  
  console.log('Test Grid:', testGrid);
  console.log('---');
  
  testFormulas.forEach(formula => {
    try {
      const result = evaluateFormula(formula, testGrid);
      console.log(`${formula} = ${result}`);
    } catch (error) {
      console.error(`Hata: ${formula}`, error);
    }
  });
  
  console.log('=== TEST TAMAMLANDI ===');
};

// Debug fonksiyonu - belirli bir formülü test et
const debugFormula = (formula, grid) => {
  console.log('=== FORMÜL DEBUG ===');
  console.log('Formül:', formula);
  console.log('Grid:', grid);
  
  try {
    const result = evaluateFormula(formula, grid);
    console.log('Sonuç:', result);
    return result;
  } catch (error) {
    console.error('Debug hatası:', error);
    return '#ERROR!';
  }
};

export {
  evaluateFormula,
  parseCellReference,
  parseRangeReference,
  getCellValue,
  getCellValueAsString,
  getRangeValues,
  allFunctions,
  testFormulaEngine,
  debugFormula,
  adaptFormulaForCell,
  applyFormulaToSelection,
  buildDependencyGraph,
  extractCellReferences,
  getCalculationOrder
};
